<template>
  <main class="mx-auto w-full max-w-7xl px-4 py-6 text-slate-900">
    <section class="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <div class="h-1.5 bg-sky-500"></div>
      <div class="flex flex-col gap-5 p-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.18em] text-sky-600">CV Matcher</p>
          <h1 class="mt-3 text-3xl font-bold leading-tight text-slate-950">Standalone CV to Company Matches</h1>
        </div>
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          @click="loadUploads"
        >
          Refresh
        </button>
      </div>
    </section>

    <section class="mt-6 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div class="grid gap-4 lg:grid-cols-[180px_minmax(0,1fr)_auto] lg:items-center">
        <div>
          <h2 class="text-lg font-bold text-slate-950">Upload CV</h2>
          <p class="mt-1 text-sm leading-6 text-slate-600">PDF, DOC, and DOCX files.</p>
        </div>

        <label class="flex min-h-[74px] cursor-pointer items-center justify-between gap-4 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-3 transition hover:border-sky-400 hover:bg-sky-50">
          <input class="sr-only" type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" @change="handleFileSelected" />
          <span class="min-w-0">
            <span class="block truncate text-sm font-semibold text-slate-800">{{ selectedFile ? selectedFile.name : 'Select a resume' }}</span>
            <span class="mt-1 block text-xs text-slate-500">{{ selectedFile ? formatFileSize(selectedFile.size) : 'Queued for background processing after upload.' }}</span>
          </span>
          <span class="shrink-0 rounded-md bg-white px-3 py-2 text-xs font-semibold text-slate-700 ring-1 ring-slate-200">Browse</span>
        </label>

        <div class="flex flex-wrap gap-3 lg:justify-end">
          <button
            type="button"
            class="inline-flex items-center justify-center rounded-lg bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="!selectedFile || uploading"
            @click="uploadResume"
          >
            <span v-if="uploading" class="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"></span>
            Upload
          </button>
          <button
            v-if="selectedFile"
            type="button"
            class="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            @click="clearSelectedFile"
          >
            Clear
          </button>
        </div>
      </div>

      <div v-if="uploadError" class="mt-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
        {{ uploadError }}
      </div>
    </section>

    <section class="mt-6 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 class="text-lg font-bold text-slate-950">Recent uploads</h2>
          <p class="mt-1 text-sm text-slate-500">Open one result at a time. Pending and processing rows refresh automatically.</p>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <span class="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{{ uploads.length }} total</span>
          <span v-if="uploads.length" class="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
            Showing {{ pageStart }}-{{ pageEnd }}
          </span>
        </div>
      </div>

      <div v-if="loading" class="mt-5 space-y-3">
        <div v-for="item in 3" :key="item" class="h-16 animate-pulse rounded-lg bg-slate-100"></div>
      </div>

      <div v-else-if="uploads.length === 0" class="mt-5 rounded-lg border border-slate-200 bg-slate-50 px-5 py-8 text-center">
        <p class="font-semibold text-slate-900">No CVs uploaded yet</p>
        <p class="mt-1 text-sm text-slate-500">Uploaded resumes will appear here with their processing status.</p>
      </div>

      <div v-else class="mt-5 overflow-hidden rounded-lg border border-slate-200">
        <article
          v-for="upload in paginatedUploads"
          :key="upload.id"
          class="border-b border-slate-200 last:border-b-0"
        >
          <div class="grid gap-3 px-4 py-3 md:grid-cols-[minmax(0,1fr)_120px_170px]">
            <div class="min-w-0">
              <p class="truncate text-sm font-semibold text-slate-950">{{ upload.original_filename }}</p>
              <p v-if="upload.candidate?.name" class="mt-1 truncate text-sm font-semibold text-sky-700">{{ upload.candidate.name }}</p>
              <p class="mt-1 text-xs text-slate-500">Uploaded {{ formatDate(upload.uploaded_at) }}</p>
              <p v-if="upload.processing_completed_at" class="mt-1 text-xs text-slate-500">Generated {{ formatDate(upload.processing_completed_at) }}</p>
            </div>
            <div class="flex items-start md:justify-center">
              <span class="rounded-full px-2.5 py-1 text-xs font-semibold" :class="statusClass(upload.status)">
                {{ statusLabel(upload.status) }}
              </span>
            </div>
            <div class="flex flex-wrap items-start gap-2 md:justify-end">
              <button
                v-if="upload.status === 'completed'"
                type="button"
                class="rounded-md px-3 py-2 text-xs font-semibold transition"
                :class="isSelectedUpload(upload) ? 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50' : 'bg-slate-950 text-white hover:bg-slate-800'"
                @click="selectUpload(upload)"
              >
                {{ isSelectedUpload(upload) ? 'Hide results' : 'View results' }}
              </button>
              <button
                v-if="upload.status === 'failed'"
                type="button"
                class="rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                @click="retryUpload(upload.id)"
              >
                Retry
              </button>
            </div>
          </div>

          <div v-if="isSelectedUpload(upload)" class="border-t border-slate-200 bg-slate-50 px-4 py-5">
            <section class="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div class="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p class="text-xs font-semibold uppercase tracking-[0.18em] text-sky-600">Result</p>
                  <h2 class="mt-2 text-2xl font-bold text-slate-950">{{ resultTitle(selectedUpload) }}</h2>
                  <p class="mt-2 text-sm text-slate-500">
                    {{ selectedUpload.original_filename }} · Generated {{ formatDate(selectedUpload.processing_completed_at) }}
                  </p>
                </div>
                <div class="flex flex-wrap gap-2">
                  <button
                    type="button"
                    class="rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-800 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
                    :disabled="regeneratingUploadId === selectedUpload.id"
                    @click="regenerateResults(selectedUpload.id)"
                  >
                    {{ regeneratingUploadId === selectedUpload.id ? 'Regenerating...' : 'Regenerate results' }}
                  </button>
                  <button
                    type="button"
                    class="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                    :disabled="downloadLoading === 'resume'"
                    @click="downloadCvMatcherFile('resume')"
                  >
                    {{ downloadLoading === 'resume' ? 'Downloading...' : 'Download resume' }}
                  </button>
                  <button
                    type="button"
                    class="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                    :disabled="downloadLoading === 'parsed'"
                    @click="downloadCvMatcherFile('parsed')"
                  >
                    {{ downloadLoading === 'parsed' ? 'Downloading...' : 'Download parsed resume' }}
                  </button>
                </div>
              </div>

              <div v-if="selectedUpload.candidate" class="mt-5 flex flex-wrap gap-2">
                <span v-if="selectedUpload.candidate.name" class="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{{ selectedUpload.candidate.name }}</span>
                <span v-if="selectedUpload.candidate.location" class="rounded-full bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700">{{ selectedUpload.candidate.location }}</span>
                <span v-if="selectedUpload.candidate.current_company" class="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">{{ selectedUpload.candidate.current_company }}</span>
              </div>

              <div v-if="selectedUpload.result" class="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div class="grid gap-3 md:grid-cols-4">
                  <div class="rounded-md bg-white p-3 ring-1 ring-slate-200">
                    <p class="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Available weight</p>
                    <p class="mt-1 text-lg font-bold text-slate-950">{{ selectedUpload.result.scoring?.available_weight || 0 }} / {{ selectedUpload.result.scoring?.formula_max || 100 }}</p>
                  </div>
                  <div class="rounded-md bg-white p-3 ring-1 ring-slate-200">
                    <p class="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Signal coverage</p>
                    <p class="mt-1 text-lg font-bold text-slate-950">{{ formatPercent(selectedUpload.result.scoring?.signal_coverage) }}</p>
                  </div>
                  <div class="rounded-md bg-white p-3 ring-1 ring-slate-200">
                    <p class="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Confidence</p>
                    <p class="mt-1 text-lg font-bold text-slate-950">{{ formatPercent(selectedUpload.result.scoring?.evidence_confidence) }}</p>
                  </div>
                  <div class="rounded-md bg-white p-3 ring-1 ring-slate-200">
                    <p class="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Source employers</p>
                    <p class="mt-1 text-lg font-bold text-slate-950">{{ selectedUpload.result.source?.matched_employer_count || 0 }} / {{ selectedUpload.result.source?.eligible_employer_count || 0 }}</p>
                  </div>
                </div>

                <div class="mt-4 grid gap-4 lg:grid-cols-2">
                  <div>
                    <p class="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Matched source employers</p>
                    <div class="mt-2 flex flex-wrap gap-1.5">
                      <span
                        v-for="employer in selectedUpload.result.source?.matched_previous_employers || []"
                        :key="`${selectedUpload.id}-${employer.company_id}-${employer.employer_name}`"
                        class="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-100"
                      >
                        {{ employer.employer_name }} -> {{ employer.company_name }} · {{ employer.confidence }}%
                      </span>
                      <span v-if="!(selectedUpload.result.source?.matched_previous_employers || []).length" class="text-sm text-slate-500">None</span>
                    </div>
                  </div>
                  <div>
                    <p class="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Candidate signals used</p>
                    <div class="mt-2 flex flex-wrap gap-1.5">
                      <span v-for="signal in candidateSignalSummary(selectedUpload.result)" :key="signal" class="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700 ring-1 ring-slate-200">
                        {{ signal }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="mt-4 rounded-md bg-white p-3 ring-1 ring-slate-200">
                  <p class="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Ranking order</p>
                  <p class="mt-2 text-sm leading-6 text-slate-700">
                    Companies are sorted by match %, earned score, commodity score, project-stage score, company-size score, location score, weighted commodity support, weighted stage support, size rank, project count, then company name.
                  </p>
                </div>
              </div>

              <div v-if="downloadError" class="mt-5 rounded-lg border border-rose-200 bg-rose-50 px-5 py-4 text-sm leading-6 text-rose-800">
                {{ downloadError }}
              </div>

              <div v-if="regenerateError" class="mt-5 rounded-lg border border-rose-200 bg-rose-50 px-5 py-4 text-sm leading-6 text-rose-800">
                {{ regenerateError }}
              </div>

              <div v-if="selectedUpload.result?.outcome !== 'matches_found'" class="mt-5 rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 text-sm leading-6 text-amber-900">
                <p>No viable Top 3 result was generated. Outcome: {{ selectedUpload.result?.outcome || 'unknown' }}.</p>
                <div v-if="resumeCompanies(selectedUpload).length" class="mt-4">
                  <p class="text-xs font-bold uppercase tracking-[0.14em] text-amber-800">Companies found in resume</p>
                  <div class="mt-2 flex flex-wrap gap-2">
                    <span v-for="company in resumeCompanies(selectedUpload)" :key="`${selectedUpload.id}-${company}`" class="rounded-full bg-white px-3 py-1 text-xs font-semibold text-amber-900 ring-1 ring-amber-200">
                      {{ company }}
                    </span>
                  </div>
                </div>
              </div>

              <div v-else class="mt-5 space-y-3">
                <article
                  v-for="(company, index) in selectedUpload.result.matches"
                  :key="company.company_id"
                  class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <div class="flex items-start justify-between gap-3">
                    <div class="flex min-w-0 items-start gap-3">
                      <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold" :class="rankClass(index)">
                        {{ index + 1 }}
                      </div>
                      <div class="min-w-0">
                        <p class="truncate text-base font-bold text-slate-950">{{ company.company_name }}</p>
                        <p v-if="company.industry_subcategory" class="mt-1 text-xs font-semibold text-sky-700">{{ company.industry_subcategory }}</p>
                      </div>
                    </div>
                    <div class="shrink-0 rounded-lg bg-emerald-50 px-2 py-2 text-center text-emerald-700 ring-1 ring-emerald-100">
                      <p class="text-sm font-bold">{{ company.total_match_percent }}%</p>
                      <p class="text-[11px] font-semibold">Match</p>
                    </div>
                  </div>

                  <div class="mt-4 flex flex-wrap gap-1.5">
                    <span v-for="signal in matchSignals(company)" :key="`${company.company_id}-${signal}`" class="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                      {{ signal }}
                    </span>
                  </div>
                  <p class="mt-3 text-xs leading-5 text-slate-500">{{ company.explanation }}</p>

                  <div class="mt-4 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      class="rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                      @click="toggleCompanyDetails(company.company_id)"
                    >
                      {{ isCompanyExpanded(company.company_id) ? 'Hide formula details' : 'Show formula details' }}
                    </button>
                    <span class="text-xs font-semibold text-slate-500">
                      {{ formatScore(company.raw_earned_score) }} / {{ formatScore(company.raw_score_max) }} points
                    </span>
                  </div>

                  <div v-if="isCompanyExpanded(company.company_id)" class="mt-4 space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <div class="grid gap-3 md:grid-cols-4">
                      <div class="rounded-md bg-white p-3 ring-1 ring-slate-200">
                        <p class="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Match score</p>
                        <p class="mt-1 text-base font-bold text-slate-950">{{ formatPercent(company.total_match_percent) }}</p>
                      </div>
                      <div class="rounded-md bg-white p-3 ring-1 ring-slate-200">
                        <p class="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Earned points</p>
                        <p class="mt-1 text-base font-bold text-slate-950">{{ formatScore(company.raw_earned_score) }}</p>
                      </div>
                      <div class="rounded-md bg-white p-3 ring-1 ring-slate-200">
                        <p class="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Available points</p>
                        <p class="mt-1 text-base font-bold text-slate-950">{{ formatScore(company.raw_score_max) }}</p>
                      </div>
                      <div class="rounded-md bg-white p-3 ring-1 ring-slate-200">
                        <p class="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Confidence</p>
                        <p class="mt-1 text-base font-bold text-slate-950">{{ formatPercent(company.evidence_confidence) }}</p>
                      </div>
                    </div>

                    <div class="overflow-hidden rounded-lg border border-slate-200 bg-white">
                      <div class="hidden bg-slate-100 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500 md:grid md:grid-cols-[150px_90px_90px_minmax(0,1fr)]">
                        <span>Component</span>
                        <span>Score</span>
                        <span>Fit</span>
                        <span>Evidence</span>
                      </div>
                      <div
                        v-for="row in componentRows(company)"
                        :key="`${company.company_id}-${row.key}`"
                        class="grid gap-2 border-t border-slate-200 px-3 py-3 first:border-t-0 md:grid-cols-[150px_90px_90px_minmax(0,1fr)]"
                      >
                        <div>
                          <p class="text-sm font-bold text-slate-950">{{ row.label }}</p>
                          <p class="mt-1 text-xs text-slate-500">Weight {{ row.weight }}</p>
                        </div>
                        <div>
                          <p class="text-sm font-bold text-slate-950">{{ formatScore(row.score) }}</p>
                          <p class="mt-1 text-xs text-slate-500">of {{ formatScore(row.weight) }}</p>
                        </div>
                        <div>
                          <p class="text-sm font-bold text-slate-950">{{ formatPercent(row.fitPercent) }}</p>
                          <p v-if="!row.available" class="mt-1 text-xs text-amber-700">Unavailable</p>
                        </div>
                        <div class="space-y-2 break-words text-xs leading-5 text-slate-600">
                          <p><span class="font-semibold text-slate-800">Matched:</span> {{ listText(row.matched) }}</p>
                          <p><span class="font-semibold text-slate-800">Candidate:</span> {{ listText(row.candidate) }}</p>
                          <p><span class="font-semibold text-slate-800">Company:</span> {{ listText(row.company) }}</p>
                          <p v-if="row.missingReason" class="text-amber-700">{{ row.missingReason }}</p>
                        </div>
                      </div>
                    </div>

                    <div class="grid gap-4 lg:grid-cols-2">
                      <div class="rounded-lg border border-slate-200 bg-white p-3">
                        <p class="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Ranking tie-breakers</p>
                        <dl class="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 break-words text-xs">
                          <dt class="text-slate-500">Commodity points</dt>
                          <dd class="text-right font-semibold text-slate-900">{{ formatScore(company.commodity_score) }}</dd>
                          <dt class="text-slate-500">Project-stage points</dt>
                          <dd class="text-right font-semibold text-slate-900">{{ formatScore(company.project_stage_score) }}</dd>
                          <dt class="text-slate-500">Company-size points</dt>
                          <dd class="text-right font-semibold text-slate-900">{{ formatScore(company.company_size_score) }}</dd>
                          <dt class="text-slate-500">Location points</dt>
                          <dd class="text-right font-semibold text-slate-900">{{ formatScore(company.location_score) }}</dd>
                          <dt class="text-slate-500">Commodity support</dt>
                          <dd class="text-right font-semibold text-slate-900">{{ formatScore(company.weighted_matched_commodity_support) }}</dd>
                          <dt class="text-slate-500">Stage support</dt>
                          <dd class="text-right font-semibold text-slate-900">{{ formatScore(company.weighted_matched_stage_support) }}</dd>
                          <dt class="text-slate-500">Size rank</dt>
                          <dd class="text-right font-semibold text-slate-900">{{ company.size_match_rank || 0 }}</dd>
                          <dt class="text-slate-500">Projects counted</dt>
                          <dd class="text-right font-semibold text-slate-900">{{ company.projects_count || 0 }}</dd>
                        </dl>
                      </div>

                      <div class="rounded-lg border border-slate-200 bg-white p-3">
                        <p class="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Company project data</p>
                        <dl class="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 break-words text-xs">
                          <dt class="text-slate-500">Projects with commodities</dt>
                          <dd class="text-right font-semibold text-slate-900">{{ company.projects_with_commodities_count || 0 }}</dd>
                          <dt class="text-slate-500">Projects with stages</dt>
                          <dd class="text-right font-semibold text-slate-900">{{ company.projects_with_stage_count || 0 }}</dd>
                          <dt class="text-slate-500">Projects with states</dt>
                          <dd class="text-right font-semibold text-slate-900">{{ company.projects_with_state_count || 0 }}</dd>
                          <dt class="text-slate-500">Employee size band</dt>
                          <dd class="text-right font-semibold text-slate-900">{{ company.company_size_band || 'Unknown' }}</dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </article>
              </div>
            </section>
          </div>
        </article>
      </div>

      <div v-if="totalPages > 1" class="mt-5 flex flex-wrap items-center justify-between gap-3">
        <p class="text-sm text-slate-500">Page {{ currentPage }} of {{ totalPages }}</p>
        <div class="flex flex-wrap gap-2">
          <button
            type="button"
            class="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="currentPage === 1"
            @click="changePage(currentPage - 1)"
          >
            Previous
          </button>
          <button
            v-for="page in visiblePages"
            :key="`page-${page}`"
            type="button"
            class="rounded-md px-3 py-2 text-sm font-semibold transition"
            :class="page === currentPage ? 'bg-slate-950 text-white' : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'"
            @click="changePage(page)"
          >
            {{ page }}
          </button>
          <button
            type="button"
            class="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="currentPage === totalPages"
            @click="changePage(currentPage + 1)"
          >
            Next
          </button>
        </div>
      </div>
    </section>

    <section v-if="failedUploads.length" class="mt-6 rounded-lg border border-rose-200 bg-rose-50 p-5 text-sm text-rose-800">
      <p class="font-semibold">Recent failures</p>
      <ul class="mt-2 space-y-1">
        <li v-for="upload in failedUploads" :key="`failed-${upload.id}`">
          {{ upload.original_filename }}: {{ upload.error?.message || 'Processing failed.' }}
        </li>
      </ul>
    </section>
  </main>
</template>

<script>
import adminService from '@/services/marketplace/admin.service'

export default {
  name: 'AdminCvMatcher',
  inject: ['toggleLoading'],
  data() {
    return {
      uploads: [],
      selectedUpload: null,
      selectedFile: null,
      uploadError: '',
      loading: false,
      uploading: false,
      pollTimer: null,
      expandedCompanyId: null,
      downloadLoading: '',
      downloadError: '',
      regeneratingUploadId: '',
      regenerateError: '',
      currentPage: 1,
      pageSize: 6,
    }
  },
  computed: {
    hasActiveUploads() {
      return this.uploads.some((upload) => ['pending', 'processing'].includes(upload.status))
    },
    failedUploads() {
      return this.uploads.filter((upload) => upload.status === 'failed').slice(0, 3)
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.uploads.length / this.pageSize))
    },
    paginatedUploads() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.uploads.slice(start, start + this.pageSize)
    },
    pageStart() {
      if (this.uploads.length === 0) return 0
      return ((this.currentPage - 1) * this.pageSize) + 1
    },
    pageEnd() {
      return Math.min(this.currentPage * this.pageSize, this.uploads.length)
    },
    visiblePages() {
      const pages = []
      const start = Math.max(1, this.currentPage - 2)
      const end = Math.min(this.totalPages, start + 4)

      for (let page = start; page <= end; page += 1) {
        pages.push(page)
      }

      return pages
    },
  },
  created() {
    this.loadUploads()
  },
  beforeUnmount() {
    this.stopPolling()
  },
  methods: {
    async loadUploads() {
      this.loading = true
      try {
        const response = await adminService.getCvMatcherUploads()
        this.uploads = response.data.uploads || []
        this.ensureCurrentPage()
        this.refreshSelectedUpload()
        this.updatePolling()
      } catch (error) {
        if (error?.response?.status === 401) {
          this.$router.push('/dashboard/auth')
          return
        }
        this.uploadError = 'Unable to load CV matcher uploads.'
      } finally {
        this.loading = false
      }
    },
    handleFileSelected(event) {
      this.uploadError = ''
      const file = event.target.files?.[0]
      if (!file) return

      const extension = file.name.split('.').pop().toLowerCase()
      if (!['pdf', 'doc', 'docx'].includes(extension)) {
        this.uploadError = 'Please select a PDF, DOC, or DOCX file.'
        event.target.value = ''
        return
      }

      this.selectedFile = file
    },
    clearSelectedFile() {
      this.selectedFile = null
      this.uploadError = ''
    },
    async uploadResume() {
      if (!this.selectedFile) return

      this.uploading = true
      this.uploadError = ''
      const formData = new FormData()
      formData.append('resume', this.selectedFile)

      try {
        await adminService.uploadCvMatcherResume(formData)
        this.selectedFile = null
        this.currentPage = 1
        await this.loadUploads()
      } catch (error) {
        this.uploadError = error?.response?.data?.error || 'Unable to upload resume.'
      } finally {
        this.uploading = false
      }
    },
    async retryUpload(id) {
      try {
        await adminService.retryCvMatcherUpload(id)
        await this.loadUploads()
      } catch (error) {
        this.uploadError = error?.response?.data?.error || 'Unable to retry upload.'
      }
    },
    selectUpload(upload) {
      if (this.selectedUpload?.id === upload.id) {
        this.selectedUpload = null
        this.expandedCompanyId = null
        this.downloadError = ''
        this.regenerateError = ''
        return
      }

      this.selectedUpload = upload
      this.expandedCompanyId = null
      this.downloadError = ''
      this.regenerateError = ''
    },
    isSelectedUpload(upload) {
      return this.selectedUpload?.id === upload.id
    },
    refreshSelectedUpload() {
      if (!this.selectedUpload) return

      const updated = this.uploads.find((upload) => upload.id === this.selectedUpload.id)
      if (updated) {
        this.selectedUpload = updated
      } else {
        this.selectedUpload = null
        this.expandedCompanyId = null
      }
    },
    ensureCurrentPage() {
      if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages
      }

      if (this.currentPage < 1) {
        this.currentPage = 1
      }
    },
    changePage(page) {
      this.currentPage = Math.min(Math.max(page, 1), this.totalPages)
      this.selectedUpload = null
      this.expandedCompanyId = null
      this.downloadError = ''
      this.regenerateError = ''
    },
    updatePolling() {
      if (this.hasActiveUploads && !this.pollTimer) {
        this.pollTimer = window.setInterval(() => this.loadUploads(), 10000)
      } else if (!this.hasActiveUploads) {
        this.stopPolling()
      }
    },
    stopPolling() {
      if (this.pollTimer) {
        window.clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    formatDate(value) {
      if (!value) return 'N/A'
      return new Date(value).toLocaleString()
    },
    formatFileSize(size) {
      if (!size) return ''
      if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`
      return `${(size / (1024 * 1024)).toFixed(1)} MB`
    },
    statusLabel(status) {
      return {
        pending: 'Pending',
        processing: 'Processing',
        completed: 'Completed',
        failed: 'Failed',
      }[status] || status
    },
    statusClass(status) {
      return {
        pending: 'bg-amber-100 text-amber-800',
        processing: 'bg-sky-100 text-sky-800',
        completed: 'bg-emerald-100 text-emerald-800',
        failed: 'bg-rose-100 text-rose-800',
      }[status] || 'bg-slate-100 text-slate-700'
    },
    rankClass(index) {
      return [
        'bg-amber-100 text-amber-800',
        'bg-slate-100 text-slate-700',
        'bg-orange-100 text-orange-800',
      ][index] || 'bg-slate-100 text-slate-700'
    },
    resultTitle(upload) {
      return upload?.candidate?.name || upload?.original_filename || 'CV match result'
    },
    async downloadCvMatcherFile(type) {
      if (!this.selectedUpload?.id) return

      this.downloadLoading = type
      this.downloadError = ''

      try {
        const response = type === 'parsed'
          ? await adminService.downloadCvMatcherParsedResume(this.selectedUpload.id)
          : await adminService.downloadCvMatcherResume(this.selectedUpload.id)

        this.saveBlobResponse(response, type === 'parsed' ? 'parsed-resume.json' : this.selectedUpload.original_filename)
      } catch (error) {
        this.downloadError = await this.downloadErrorMessage(error)
      } finally {
        this.downloadLoading = ''
      }
    },
    async regenerateResults(id) {
      if (!id || this.regeneratingUploadId) return

      this.regeneratingUploadId = id
      this.regenerateError = ''
      this.downloadError = ''

      try {
        const response = await adminService.regenerateCvMatcherResults(id)
        const updatedUpload = response.data?.upload
        if (updatedUpload) {
          const index = this.uploads.findIndex((upload) => upload.id === updatedUpload.id)
          if (index !== -1) {
            this.uploads.splice(index, 1, updatedUpload)
          }
          if (this.selectedUpload?.id === updatedUpload.id) {
            this.selectedUpload = updatedUpload
            this.expandedCompanyId = null
          }
        } else {
          await this.loadUploads()
        }
        this.$root.notify('CV matcher results regenerated', 'success')
      } catch (error) {
        this.regenerateError = error?.response?.data?.error || 'Unable to regenerate CV matcher results.'
      } finally {
        this.regeneratingUploadId = ''
      }
    },
    saveBlobResponse(response, fallbackFilename) {
      const blob = new Blob([response.data], {
        type: response.headers?.['content-type'] || 'application/octet-stream',
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')

      link.href = url
      link.download = this.filenameFromDisposition(response.headers?.['content-disposition']) || fallbackFilename || 'download'
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    },
    filenameFromDisposition(disposition) {
      if (!disposition) return ''

      const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i)
      if (utfMatch?.[1]) {
        return decodeURIComponent(utfMatch[1])
      }

      const match = disposition.match(/filename="?([^";]+)"?/i)
      return match?.[1] || ''
    },
    async downloadErrorMessage(error) {
      const data = error?.response?.data
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text())
          return parsed.error || 'Unable to download file.'
        } catch {
          return 'Unable to download file.'
        }
      }

      return data?.error || 'Unable to download file.'
    },
    toggleCompanyDetails(companyId) {
      this.expandedCompanyId = this.expandedCompanyId === companyId ? null : companyId
    },
    isCompanyExpanded(companyId) {
      return this.expandedCompanyId === companyId
    },
    matchSignals(company) {
      const signals = [
        ...(company.matched_commodities || []),
        ...(company.matched_project_stages || []),
        ...(company.matched_states || []),
        ...(company.matched_company_size_bands || []),
      ]
      return signals.slice(0, 4)
    },
    candidateSignalSummary(result) {
      const input = result?.input || {}
      const signals = [
        ...this.prefixedSignals('Commodity', input.commodities),
        ...this.prefixedSignals('Stage', input.project_stages),
        ...this.prefixedSignals('State', input.states),
        ...this.prefixedSignals('Size', input.company_size_bands),
      ]
      return signals.length ? signals : ['No candidate signals']
    },
    prefixedSignals(prefix, values) {
      return (values || []).map((value) => `${prefix}: ${value}`)
    },
    componentRows(company) {
      return [
        {
          key: 'commodity',
          label: 'Commodities',
          component: company.components?.commodity,
          candidate: company.candidate_commodities,
          company: company.company_project_commodities,
          matched: company.matched_commodities,
        },
        {
          key: 'project_stage',
          label: 'Project stages',
          component: company.components?.project_stage,
          candidate: company.candidate_project_stages,
          company: company.company_project_stages,
          matched: company.matched_project_stages,
        },
        {
          key: 'company_size',
          label: 'Company size',
          component: company.components?.company_size,
          candidate: company.candidate_company_size_bands,
          company: company.company_size_band ? [company.company_size_band] : [],
          matched: company.matched_company_size_bands,
        },
        {
          key: 'location',
          label: 'Location',
          component: company.components?.location,
          candidate: company.candidate_states,
          company: company.company_project_states,
          matched: company.matched_states,
        },
      ].map((row) => ({
        ...row,
        available: row.component?.available ?? false,
        weight: row.component?.weight ?? 0,
        score: row.component?.score ?? 0,
        fitPercent: row.component?.fit_percent ?? 0,
        matched: row.component?.matched || row.matched || [],
        missingReason: row.component?.missing_reason || '',
      }))
    },
    formatPercent(value) {
      const numeric = Number(value || 0)
      return `${Number.isInteger(numeric) ? numeric : numeric.toFixed(1)}%`
    },
    formatScore(value) {
      const numeric = Number(value || 0)
      return Number.isInteger(numeric) ? String(numeric) : numeric.toFixed(2).replace(/\.?0+$/, '')
    },
    listText(values) {
      return values?.length ? values.join(', ') : 'None'
    },
    resumeCompanies(upload) {
      const companies = upload?.candidate?.companies || upload?.result?.candidate?.companies || []
      return [...new Set(companies.filter(Boolean))]
    },
  },
}
</script>
